import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, throwError, from } from "rxjs";
import { map, mergeMap } from "rxjs/operators";
import { API_BASE_URL } from "./api-config";

import { User } from "../model/user";
import { PermissionEnum } from "../model/permission-enum";
import { LocalStorageService } from "./local-storage.service";
import { NativeHttpService } from "./native-http.service";

@Injectable({
  providedIn: "root",
})
export class AuthenticationService {
  readonly apiUrl = API_BASE_URL;
  private currentUserSubject: BehaviorSubject<User | null>;
  public currentUser: Observable<User | null>;
  public loggedIn: BehaviorSubject<boolean>;
  private refreshTokenTimeout: any;
  private initializationPromise: Promise<void>;

  constructor(
    private http: NativeHttpService,
    private localStorageService: LocalStorageService
  ) {
    this.currentUserSubject = new BehaviorSubject<User | null>(null);
    this.currentUser = this.currentUserSubject.asObservable();
    this.loggedIn = new BehaviorSubject<boolean>(false);
    
    this.initializationPromise = this.initializeUser();
  }
  
  public async waitForInitialization(): Promise<void> {
    await this.initializationPromise;
  }

  private async initializeUser(): Promise<void> {
    try {
      console.log('Initializing user from storage...');
      await this.localStorageService.diagnose();
      
      const currentSessionUser = await this.localStorageService.getItem("currentUser");
      console.log('Current session user:', currentSessionUser ? 'found' : 'null');
      
      if (currentSessionUser) {
        await this.loadUserPreferences(currentSessionUser);
        this.currentUserSubject.next(currentSessionUser);
        
        if (currentSessionUser.jwtToken) {
          this.loggedIn.next(true);
          this.startRefreshTokenTimer();
          console.log('User authenticated successfully');
        }
      } else {
        console.log('No stored user found');
      }
    } catch (error) {
      console.error("Error loading stored user:", error);
      await this.localStorageService.remove("currentUser");
    }
  }

  public get currentUserValue(): User | null {
    return this.currentUserSubject.value;
  }

  public async updateCurrentUser(user: User): Promise<void> {
    await this.localStorageService.setItem("currentUser", user);
    this.currentUserSubject.next(user);
  }

  private async loadUserPreferences(user: User): Promise<void> {
    const savedPreferences = await this.localStorageService.getItem("userPreferences");
    if (savedPreferences) {
      if (savedPreferences.userId === user.id) {
        user.favouriteHand = savedPreferences.favouriteHand || "right";
        user.favouriteMatch = savedPreferences.favouriteMatch || "competitive";
        user.club_id = savedPreferences.club_id || user.club_id;
        user.availableToPlay = savedPreferences.availableToPlay ?? true;
      }
    }

    if (!user.favouriteHand) {
      user.favouriteHand = "right";
    }
    if (!user.favouriteMatch) {
      user.favouriteMatch = "competitive";
    }
  }

  public get currentUserPermissions(): Array<PermissionEnum> {
    if (!this.currentUserValue || !this.currentUserValue.jwtToken) return [];
    try {
      const permissions = JSON.parse(
        atob(this.currentUserValue.jwtToken.split(".")[1])
      ).permission;
      let returnValue = permissions;
      if (!Array.isArray(permissions)) {
        returnValue = [permissions];
      }
      return returnValue;
    } catch (error) {
      console.error("Error parsing JWT token:", error);
      return [];
    }
  }

  get isLoggedIn() {
    return this.loggedIn.asObservable();
  }

  login(username: string, password: string) {
    return this.http
      .post<any>(`${this.apiUrl}/Authentication/SignIn`, { username, password })
      .pipe(
        mergeMap((user) => from(this.handleLoginSuccess(user)))
      );
  }

  private async handleLoginSuccess(user: User): Promise<User> {
    await this.loadUserPreferences(user);
    await this.localStorageService.setItem("currentUser", user);
    this.currentUserSubject.next(user);
    this.loggedIn.next(true);
    this.startRefreshTokenTimer();
    return user;
  }

  async logout(): Promise<void> {
    this.http
      .post<any>(
        `${this.apiUrl}/Authentication/SignOut`,
        {},
        { withCredentials: true }
      )
      .subscribe();
    this.stopRefreshTokenTimer();

    await this.localStorageService.remove("currentUser");
    this.currentUserSubject.next(null);
    this.loggedIn.next(false);
  }

  refreshToken() {
    const currentRefreshToken = this.currentUserValue?.refreshToken;
    if (!currentRefreshToken) {
      this.logout();
      return throwError(() => new Error("No refresh token available"));
    }

    return this.http
      .post<any>(
        `${this.apiUrl}/Authentication/RefreshToken`,
        { refreshToken: currentRefreshToken },
        { withCredentials: true }
      )
      .pipe(
        mergeMap((user) => from(this.handleLoginSuccess(user)))
      );
  }

  private startRefreshTokenTimer() {
    const jwtTokenRaw = this.currentUserValue?.jwtToken;
    if (jwtTokenRaw != null) {
      const jwtToken = JSON.parse(atob(jwtTokenRaw.split(".")[1]));
      const expires = new Date(jwtToken.exp * 1000);
      const timeout = expires.getTime() - Date.now();
      this.refreshTokenTimeout = setTimeout(() => {
        this.refreshToken().subscribe();
      }, timeout);
    }
  }

  private stopRefreshTokenTimer() {
    clearTimeout(this.refreshTokenTimeout);
  }

  generateResetPasswordEmail(userEmail: string): Observable<any> {
    return this.http.post<any>(
      `${
        this.apiUrl
      }/Authentication/GenerateResetPasswordEmail?userEmail=${encodeURIComponent(
        userEmail
      )}`,
      {}
    );
  }

  resetPassword(
    userEmail: string,
    newPassword: string,
    passwordResetToken: string
  ): Observable<any> {
    return this.http.post<any>(
      `${
        this.apiUrl
      }/Authentication/ResetPassword?userEmail=${encodeURIComponent(
        userEmail
      )}&newPassword=${encodeURIComponent(
        newPassword
      )}&passwordResetToken=${encodeURIComponent(passwordResetToken)}`,
      {}
    );
  }
}
